/**
 * Client – list my assigned programs (from coach).
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { getMyPrograms } from '../../domain/usecases/coaching';
import Card from '../../shared/ui/Card.jsx';
import Button from '../../shared/ui/Button.jsx';
import EmptyState from '../../shared/ui/EmptyState.jsx';
import Skeleton from '../../shared/ui/Skeleton.jsx';
import Display from '../../shared/ui/Display.jsx';
import Title from '../../shared/ui/Title.jsx';
import Body from '../../shared/ui/Body.jsx';
import styles from './MyProgramsPage.module.css';

export function MyProgramsPage() {
  const { userId } = useAuth();
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) return;
    getMyPrograms(userId).then(setPrograms).finally(() => setLoading(false));
  }, [userId]);

  if (!userId) {
    return (
      <div className={styles.page}>
        <EmptyState icon="user" title="Not logged in" body="Log in to see your programs." />
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <Display as="h1" size="l" style={{ marginBottom: '0.5rem' }}>My programs</Display>
      <Body tone="secondary" style={{ marginBottom: '1.25rem' }}>Programs assigned by your coach.</Body>

      {loading ? (
        <div className={styles.skeletonList}>
          {[1, 2, 3].map((i) => <Skeleton key={i} width="100%" height={80} radius="var(--radius-md)" />)}
        </div>
      ) : programs.length === 0 ? (
        <EmptyState
          icon="coach"
          title="No programs yet"
          body="No programs assigned yet. Your coach will assign one when you're ready."
        />
      ) : (
        <ul className={styles.list}>
          {programs.map((p) => (
            <li key={p.id}>
              <Card className={styles.card}>
                <div className={styles.cardBody}>
                  <div className={styles.cardInfo}>
                    <Title as="h3" style={{ fontSize: 16, lineHeight: '22px', marginBottom: 2 }}>
                      {p.title}
                    </Title>
                    <Body tone="secondary" as="p" style={{ fontSize: 13, margin: 0 }}>
                      {p.weeks ? `${p.weeks} weeks` : ''}
                      {p.startDate ? ` · Started ${p.startDate}` : ''}
                    </Body>
                  </div>
                  <Button variant="secondary" size="sm" as={Link} to={`/coaching/my-programs/${p.id}`}>
                    View
                  </Button>
                </div>
              </Card>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
